import { Image } from 'component/Image'
import { Space } from 'component/Space'
import { useLatestCallback } from 'hook/useLatestCallback'
import { itemImage } from 'page/catalog/BasicCarouselWelcome/CarouselItemsStore'
import { memo, useMemo, useRef } from 'react'
import { CoverflowEffectOptions } from 'swiper/types'
import { Autoplay, EffectCoverflow } from 'swiper/modules'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Item } from 'type/Item'
import css from './BasicCarouselWelcome.module.scss'
import 'swiper/css'

// в loop-режиме swiper не клонирует слайды, а переставляет существующие,
// поэтому на буфер по бокам нужен запас, иначе loop отключается
const MIN_SLIDES = 7
const EXTRA = 2

const COVERFLOW: CoverflowEffectOptions = {
  // боковые карточки развёрнуты к центру: ближний край — внутренний
  rotate: 26,
  // по макету центр боковой карточки в 396px от центра главной, а шаг слайда 780
  stretch: '49%',
  // боковые уводим вглубь: в 3d-контексте порядок наложения задаёт глубина, а не z-index
  depth: 100,
  scale: 0.7,
  slideShadows: false,
}

// пока карточку держат пальцем/мышью, автопрокрутку останавливаем полностью:
// autoplay.pause() при waitForTransition: false сразу же возобновляется сам
function onTouchStart(swiper: SwiperClass) {
  swiper.autoplay?.stop()
}

function onTouchEnd(swiper: SwiperClass) {
  swiper.autoplay?.start()
}

// товаров может быть меньше, чем нужно swiper для лупа — повторяем список целиком
function repeat(items: Item[]): Item[] {
  if (!items.length || items.length >= MIN_SLIDES) return items
  const times = Math.ceil(MIN_SLIDES / items.length)
  return Array.from({ length: times }, () => items).flat()
}

type Props = {
  items: Item[]
  delay: number
  onChange(index: number): void
  onTry(): void
}

// мемо обязательно: в loop-режиме swiper сбрасывает позицию, если родитель пересоздаёт слайды
export const Slides = memo(({ items, delay, onChange, onTry }: Props) => {
  const slides = useMemo(() => repeat(items), [items])
  const swiper = useRef<SwiperClass | null>(null)

  // клик по центральной карточке — примерка, по боковой — доводим её в центр
  const onSlideClick = useLatestCallback((index: number) => {
    const it = swiper.current
    if (!it) return
    if (it.realIndex === index) onTry()
    else it.slideToLoop(index)
  })

  // waitForTransition/loopPreventsSliding: false — иначе пропущенный transitionend
  // (таб в фоне) навсегда оставляет swiper в animating и карусель встаёт
  return <Swiper className={css.swiper} modules={[Autoplay, EffectCoverflow]} speed={600}
    autoplay={{ delay, disableOnInteraction: false, waitForTransition: false }}
    loop={slides.length > 2} loopPreventsSliding={false} loopAdditionalSlides={EXTRA}
    effect="coverflow" coverflowEffect={COVERFLOW}
    slidesPerView="auto" centeredSlides grabCursor
    onSwiper={value => swiper.current = value}
    onSlideChange={value => onChange(value.realIndex % items.length)}
    onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
    {slides.map((it, i) => <SwiperSlide key={`${it.item_id}-${i}`} className={css.swipe}>
      <Space className={css.slide} onClick={() => onSlideClick(i)}>
        <Image className={css.image} src={itemImage(it) ?? ''} />
      </Space>
    </SwiperSlide>)}
  </Swiper>
})

Slides.displayName = 'Slides'
